import { Ext } from "../../Doc";
import type { GuideProps } from "../Guide";

/**
 * The member guide, French translation of en.tsx. Every number comes from `limits`; keep this
 * text in step with the English source.
 */
export function GuideFr({ limits: l, link }: GuideProps) {
  return (
    <>
      <h1>Comment fonctionne Vrelis</h1>
      <p className="lead">
        Vrelis est un service d’envoi de messages chiffrés, les capsules temporelles, qui ne peuvent être lus qu’à partir d’un moment que vous choisissez : par tous
        les participants au pilote, ou par une seule personne. Avant ce moment, personne ne peut lire une capsule : ni la personne à qui vous avez écrit, ni les
        personnes qui gèrent Vrelis, ni vous.
      </p>
      <p className="warn">
        Ceci est un pilote fermé. Téléchargez les capsules que vous voulez garder (voir ci-dessous), et conservez votre phrase de récupération.
      </p>

      <h2>Pourquoi faire confiance au verrou</h2>
      <p>
        Le verrou n’est pas une promesse de Vrelis. C’est de la cryptographie publiée, plus un signal temporel public que Vrelis ne contrôle pas. Voici ce qui le
        fait tenir, et ce qu’il faudrait croire pour qu’il cède.
      </p>

      <h3>drand : une horloge publique qui signe le temps</h3>
      <ul>
        <li>
          <Ext href="https://drand.love"><b>drand</b></Ext> est un réseau géré par la{" "}
          <Ext href="https://www.cloudflare.com/leagueofentropy/">League of Entropy</Ext>, un groupe d’organisations indépendantes (parmi lesquelles Cloudflare,
          Protocol Labs, Kudelski Security, l’EPFL et l’University College London) qui publient ensemble une valeur aléatoire signée toutes les 3 secondes. Les
          valeurs sont numérotées : tour 1, tour 2, et ainsi de suite, chacune pour un moment connu. Tout le monde peut les lire depuis plusieurs relais publics ;
          personne ne peut lire une valeur future, car elle n’existe pas encore.
        </li>
        <li>
          Chaque tour est signé par cryptographie à seuil (<Ext href="https://en.wikipedia.org/wiki/BLS_digital_signature">signatures BLS</Ext>) : chaque
          organisation détient une part d’une clé secrète, et une signature exige une majorité des parts. Aucune organisation seule, ni aucune minorité d’entre
          elles, ne peut produire la signature d’un tour avant son heure. Le réseau fonctionne depuis 2020 ; la chaîne que nous utilisons,{" "}
          <Ext href="https://api.drand.sh/v2/chains/52db9ba70e0cc0f6eaf7803dd07447a1f5477735fd3f661792ba94600c84e971/info">quicknet</Ext>, depuis 2023.
          Sa clé publique est fixée dans notre code, et chaque signal que nous recevons est vérifié avec elle.
        </li>
        <li>
          <b>Le chiffrement temporel</b> (<Ext href="https://eprint.iacr.org/2023/189">tlock</Ext>, publié par des chercheurs de Protocol Labs et de Cloudflare
          en 2023) transforme cette horloge en verrou : un message est
          chiffré pour un numéro de tour, et la signature de ce tour, une fois publiée, est la seule clé qui l’ouvre. Tant que drand ne l’a pas publiée, cette clé
          n’existe nulle part au monde.
        </li>
      </ul>

      <h3>Comment Vrelis l’utilise</h3>
      <ul>
        <li>
          Votre navigateur chiffre le texte et les clés de vos fichiers pour le tour drand correspondant à l’heure choisie ; Nouvelle capsule affiche le numéro
          du tour avant l’envoi. Une capsule privée est chiffrée une seconde fois pour la clé du destinataire : après l’heure, lui seul peut l’ouvrir.
        </li>
        <li>
          Notre serveur ne reçoit que le résultat scellé et le stocke. Il ne détient aucune clé qui ouvre une capsule : ni avant l’heure, ni après pour une capsule
          privée. Une fuite de la base de données exposerait du texte chiffré et des métadonnées, jamais un message.
        </li>
        <li>
          Le moment venu, votre navigateur (ou l’outil d’ouverture hors ligne) récupère la signature du tour auprès de l’un des quatre relais publics de drand,
          la vérifie avec la clé publique fixée, et déchiffre. Vrelis ne joue aucun rôle dans cette étape.
        </li>
        <li>
          Chaque capsule est signée par la clé de son auteur (<Ext href="https://en.wikipedia.org/wiki/Secp256k1">secp256k1</Ext>, le même type de clé que celui
          de Bitcoin et de <Ext href="https://nostr.com">Nostr</Ext>), donc une capsule modifiée est détectée. La clé est créée dans votre navigateur à partir de
          vos 12 mots (
          <Ext href="https://github.com/bitcoin/bips/blob/master/bip-0039.mediawiki">BIP-39</Ext>) et n’en sort jamais sans être chiffrée.
        </li>
      </ul>

      <h3>Si Vrelis disparaît</h3>
      <ul>
        <li>
          Les formats sont ouverts : une capsule est un <Ext href="https://github.com/nostr-protocol/nips/blob/master/01.md">événement Nostr</Ext> (un{" "}
          <Ext href="https://github.com/nostr-protocol/nips/blob/master/59.md">gift wrap NIP-59</Ext> pour une capsule privée) dont le contenu est un fichier{" "}
          <Ext href="https://age-encryption.org">age</Ext> avec une strophe <Ext href="https://github.com/drand/tlock-js">tlock</Ext>. Tout logiciel qui
          implémente ces spécifications ouvertes peut ouvrir vos capsules ; le nôtre n’est pas nécessaire.
        </li>
        <li>
          <b>Télécharger</b> garde les capsules sur votre propre disque, avec l’outil d’ouverture hors ligne : un seul fichier HTML qui les ouvre sur votre
          ordinateur, sans serveur Vrelis, à l’aide du signal drand requis et, pour une capsule privée, de votre clé. Un export complet peut être ouvert sans
          vrelis.com si le signal requis est disponible et si vous conservez les fichiers et les clés dont il a besoin.
        </li>
        <li>
          Si drand s’arrêtait un jour avant de produire le signal futur requis, ces capsules pourraient rester verrouillées pour toujours. Les télécharger à
          l’avance ne supprime pas ce risque. Une capsule dont la date d’ouverture est passée a toujours besoin de son signal exact et vérifié : sans ce signal
          dans votre archive ou disponible auprès d’un relais, la date seule ne permet pas de l’ouvrir hors ligne. Conservez des exports complets et les clés
          qu’ils exigent.
        </li>
      </ul>

      <h3>Ce qu’il faudrait croire pour qu’il cède</h3>
      <ul>
        <li>
          Qu’une majorité des organisations de drand s’entendent pour signer un tour en avance. Elles sont indépendantes, réparties dans plusieurs pays, et la
          chaîne est publique : une telle signature serait visible de tous. C’est l’hypothèse de confiance de tout le système.
        </li>
        <li>
          Que la page web mente. La page est du code que Vrelis envoie à votre navigateur ; quiconque contrôle ce code pourrait lire le texte avant qu’il soit
          scellé, ou votre clé après que vous l’avez déverrouillée. C’est pourquoi les capsules peuvent être téléchargées et ouvertes hors ligne, pourquoi les
          empreintes des clés vous sont montrées pour que vous les compariez en dehors de Vrelis, et pourquoi une application mobile qui garde la clé hors de la
          page web est prévue après le pilote.
        </li>
        <li>
          Que la cryptographie actuelle soit cassée. La cryptographie sur courbes elliptiques, qu’utilisent à la fois drand et votre clé, pourrait être cassée
          par de futurs{" "}
          <Ext href="https://en.wikipedia.org/wiki/Post-quantum_cryptography">ordinateurs quantiques</Ext>.
          Pour des capsules censées rester scellées pendant des décennies, c’est un risque réel que le pilote ne résout pas.
        </li>
      </ul>

      <h2>Accéder au service</h2>
      <ol>
        <li>
          Si l’administrateur a approuvé votre adresse, inscrivez-vous directement avec elle ; sinon, ouvrez votre lien d’invitation et utilisez son adresse.
          Connectez-vous avec ce compte Google (Google ne communique à Vrelis que votre nom et votre adresse e-mail), ou avec un mot de passe (nous envoyons un
          lien par e-mail pour confirmer l’adresse). Plus tard, vous pourrez ajouter une clé d’accès pour vous connecter.
        </li>
        <li>
          Vrelis vous propose de créer votre <b>clé</b> dans votre navigateur : votre signature principale, qui signe ce que vous envoyez et ouvre les capsules
          privées qui vous sont envoyées. Vous pouvez passer cette étape et la créer plus tard dans Compte, Clés ; sans clé, vous ne lisez que les capsules
          publiques. Avant que votre <b>phrase de récupération</b> (12 mots qui reconstruisent la clé sur n’importe quel appareil) s’affiche, on vous demande de
          vérifier que personne ne peut voir ni enregistrer votre écran.
        </li>
        <li>Écrivez les 12 mots sur papier. On vous demande d’en saisir trois, puis de vous reconnecter pour confirmer que c’est vous : avec votre mot de passe ou une clé d’accès, ou avec Google pour votre toute première clé.</li>
        <li>
          Choisissez un PIN (au moins {l.pinDigits} chiffres) et créez une <Ext href="https://passkeys.dev/docs/intro/what-are-passkeys/">clé d’accès</Ext> (empreinte
          digitale, visage ou PIN de votre appareil). Ensemble, ils protègent la clé sur cet appareil
          et la copie que Vrelis garde pour le passage à un nouvel appareil. Si votre navigateur ne peut pas utiliser une clé d’accès de cette façon, la clé reste
          sans protection dans ce navigateur et il n’y a pas de copie sur le serveur ; la phrase est alors le seul moyen de passer à un autre appareil.
        </li>
      </ol>

      <h2>Votre phrase de récupération</h2>
      <ul>
        <li>Gardez-la sur papier, en lieu sûr. Ne la photographiez pas et ne l’enregistrez pas sur un ordinateur ni dans un gestionnaire de mots de passe.</li>
        <li>
          Quiconque possède les 12 mots <b>est</b> vous : il peut signer à votre place et ouvrir vos capsules privées. S’ils fuitent, utilisez{" "}
          <b>Réinitialiser la clé</b> dans Compte, Clés : vous saisissez la phrase, vous vous reconnectez, et la clé est révoquée définitivement ; personne ne
          peut plus envoyer avec elle ni vers elle. Vous indiquez depuis quand elle a pu être volée, et les capsules envoyées avec elle à partir de ce moment sont
          marquées « clé révoquée ». Ensuite, vous créez une nouvelle clé avec une nouvelle phrase.
        </li>
        <li>
          Vos anciennes clés peuvent aller dans un <b>trousseau</b>, chiffré avec votre clé actuelle, pour que les capsules envoyées à une ancienne clé s’ouvrent
          toujours sans l’ancienne phrase. Vous pouvez aussi conserver vous-même l’ancienne phrase et l’ajouter au trousseau plus tard.
        </li>
        <li>
          Si vous perdez un appareil : connectez-vous sur un autre et utilisez votre clé d’accès et votre PIN (si votre clé d’accès est synchronisée) ou votre
          phrase. Si vous oubliez le PIN : utilisez la phrase. Après {l.pinAttempts} PIN erronés, la copie sur le serveur est bloquée ; la phrase en écrit une
          nouvelle.
        </li>
        <li>
          Si vous perdez la phrase <b>et</b> tous les appareils qui contiennent votre clé, vos capsules privées ne pourront jamais être ouvertes, par personne.
          Un administrateur peut seulement vous donner une nouvelle identité. La révocation d’une clé exige aussi la phrase : sans elle, c’est un administrateur
          qui la révoque pour vous.
        </li>
      </ul>

      <h2>Écrire une capsule</h2>
      <ul>
        <li>
          <b>Tous les participants au pilote</b> (publique) ou <b>un seul membre</b> (privée). Une capsule publique peut être lue par tous les membres après son
          heure ; une capsule privée seulement par la personne à qui vous avez écrit, et par vous si vous gardez une copie (activée par défaut).
        </li>
        <li>
          Chaque capsule doit avoir un <b>objet</b>. Il n’est jamais chiffré : quiconque voit la capsule le lit immédiatement, notre serveur aussi.
        </li>
        <li>
          Une <b>introduction</b> est facultative : jusqu’à {l.introChars} caractères lisibles avant l’heure, quand on clique sur la capsule. Sur une capsule
          publique, tout le monde la lit, notre serveur aussi. Sur une capsule privée, seul le destinataire la lit, avec sa clé ; le serveur ne la voit pas. Le
          message et ses fichiers restent verrouillés dans les deux cas.
        </li>
        <li>
          L’expéditeur d’une capsule privée est scellé pour la clé du destinataire : celui-ci le voit dès qu’il ouvre la capsule avec sa clé, bien avant son
          heure. Quiconque a la capsule sans cette clé ne voit que l’objet et l’heure. Notre serveur sait quel compte l’a envoyée. Il ne le dit qu’au
          destinataire, chiffré pour sa clé, afin que l’application puisse vérifier que la clé qui a signé la capsule appartient bien à ce compte ; sinon, elle
          affiche « Expéditeur non vérifié » au lieu d’un nom.
        </li>
        <li>
          <b>Discret</b> (capsules privées) : les listes du destinataire affichent « Expéditeur discret » au lieu de votre nom jusqu’à ce qu’il clique sur la
          capsule, pour que personne ne voie par-dessus son épaule qui lui écrit. Le destinataire peut toujours voir que c’est vous, avant l’heure.
        </li>
        <li>
          Choisissez quand elle s’ouvre : dans 5 minutes, une heure, un jour, une semaine, un an, ou à une date allant jusqu’à l’année 2125. L’heure réelle
          d’ouverture s’affiche avant l’envoi ; elle est exacte à quelques secondes près.
        </li>
        <li>
          Texte : jusqu’à {l.publicText} pour une capsule publique, {l.privateText} pour une capsule privée. Fichiers : jusqu’à {l.files} fichiers et, au total,
          {" "}{l.fileBytes} avec l’offre Gratuite ou {l.planFileBytes} avec l’Ancienne offre ou Start. Les photos
          sont réduites et perdent leurs métadonnées, dont la localisation.
        </li>
        <li>
          <b>Sceller et envoyer</b> demande une fois votre PIN et votre clé d’accès, pour signer la capsule. Ensuite, personne ne peut la modifier, et personne
          ne peut la lire avant son heure.
        </li>
        <li>
          Avant d’écrire en privé à quelqu’un, vérifiez sa clé : Nouvelle capsule affiche son empreinte (16 caractères). Si sa clé a changé depuis la dernière
          fois que ce navigateur l’a vue, Nouvelle capsule vous avertit et attend que vous confirmiez avec cette personne, en dehors de Vrelis, que la nouvelle
          clé est bien la sienne.
        </li>
      </ul>

      <h2>Lire</h2>
      <ul>
        <li>
          <b>Fil public</b> : les capsules publiques. <b>Boîte de réception</b> : les capsules privées qui vous sont destinées. <b>Envoyés</b> : les vôtres.{" "}
          <b>À surveiller</b> : les capsules publiques que vous attendez. Suivez un auteur pour ne voir que ses capsules.
        </li>
        <li>
          <b>Masquer</b> un membre : ses capsules publiques quittent votre Fil public, sur tous vos appareils ; rien ne change pour lui. <b>Bloquer</b> un
          membre : il ne peut plus déposer de capsules privées dans votre Boîte de réception (Nouvelle capsule le lui indique avant l’envoi) ; vous pouvez
          toujours le suivre et lire ses capsules publiques. Ses capsules antérieures passent dans Boîte de réception, Bloqués, et s’ouvrent toujours. Les deux
          actions se trouvent dans le menu d’une personne et sur la page Personnes, avec la liste de ceux qui vous suivent.
          Vrelis ne répond que de ce qu’il reçoit et livre : personne ne peut empêcher quelqu’un de chiffrer pour votre clé publique en dehors de Vrelis.
        </li>
        <li>
          Une capsule privée n’affiche rien tant que vous ne l’avez pas ouverte : <b>Ouvrir</b> demande votre PIN, puis affiche qui l’a envoyée et quand elle
          s’ouvre, et le texte une fois cette heure venue.
        </li>
        <li>
          Votre clé reste verrouillée ; chaque action qui en a besoin demande le PIN. Pour lire plusieurs capsules à la suite, utilisez{" "}
          <b>Déverrouiller pendant … min</b> dans la barre en haut. <b>Verrouiller</b> masque tout le contenu privé d’un coup ; quitter la page aussi.
        </li>
        <li>
          Dans <a href="/account">Compte</a>, Confidentialité : « N’afficher les expéditeurs dans mes listes qu’après un clic sur une capsule » traite chaque
          capsule privée comme discrète, sur tous vos appareils.
        </li>
        <li>
          Les listes affichent 50 capsules à la fois ; <b>Afficher plus</b> ajoute les suivantes. Vrelis les filtre et les trie (par heure d’ouverture ou par
          date de réception), donc un filtre ou un groupe envoie à Vrelis les identifiants des membres choisis, jamais le groupe ni son nom. Filtrer votre Boîte
          de réception par expéditeur nécessite votre clé, exclut les capsules discrètes, et est désactivé tant que les expéditeurs ne s’affichent qu’après un
          clic.
        </li>
        <li>
          <b>Rechercher</b> porte sur les objets, l’introduction des capsules publiques, l’auteur d’une capsule publique et les personnes à qui vous avez
          envoyé. La recherche ne porte jamais sur le texte ouvert (seul votre appareil l’a) ni sur l’expéditeur d’une capsule privée reçue.
        </li>
      </ul>

      <h2>Enveloppe-réponse, CC et Correspondance</h2>
      <ul>
        <li>
          Un message privé peut contenir une <b>enveloppe-réponse</b> : vous demandez à chaque destinataire une réponse privée en retour, scellée pour votre
          clé. Il peut répondre même s’il ne vous suit pas. Vous choisissez si chaque réponse s’ouvre à une heure que vous fixez (après cette heure, aucune
          réponse n’est acceptée) ou à une heure choisie par le destinataire. Vous payez d’avance la capsule et la réponse : 2 timbres par destinataire au lieu de 1. La première réponse d’un destinataire ne lui coûte rien ; une deuxième lui coûte un timbre, et vous voyez les
          deux.
        </li>
        <li>
          <b>CC</b> : un message privé à un membre peut aller à plusieurs membres à la fois, et chaque personne listée voit qui d’autre l’a reçu. La liste est
          scellée dans le message avec la signature de l’expéditeur ; notre serveur ne la voit jamais. Un envoi en groupe sans CC reste aveugle : personne ne
          voit qui d’autre l’a reçu.
        </li>
        <li>
          Les messages avec enveloppe-réponse et leurs réponses se trouvent sur la page <b>Correspondance</b>, pas dans Boîte de réception ni dans Envoyés :
          sous chaque demande, vous voyez les réponses de chaque destinataire dans l’ordre, verrouillées jusqu’à leur heure, et qui a payé chacune. La réponse
          ne va qu’à l’expéditeur.
        </li>
      </ul>

      <h2>Garder vos capsules sans Vrelis</h2>
      <ul>
        <li>
          <b>Télécharger</b> sur une capsule l’enregistre dans un fichier ; avec des pièces jointes, c’est un .zip contenant les fichiers et l’outil d’ouverture
          hors ligne. Sélectionnez-en plusieurs et utilisez{" "}
          <b>Télécharger la sélection</b> pour obtenir une seule archive (« Tout sélectionner » prend les capsules affichées).
        </li>
        <li>
          L’outil d’ouverture hors ligne est un seul fichier HTML (<a href="/account">Compte</a>, ou dans le .zip). Il ouvre vos capsules sur votre propre
          ordinateur, les capsules privées avec votre phrase. Il
          ne contacte jamais vrelis.com ; il demande seulement au service public de temps drand le signal d’ouverture si le fichier ne le contient pas encore.
        </li>
        <li>
          « Inclure le texte et les fichiers ouverts » place le texte et les fichiers ouverts dans le téléchargement sans chiffrement : toute personne qui a le
          fichier peut les lire.
        </li>
      </ul>

      <h2>Cet appareil</h2>
      <ul>
        <li>
          <b>Verrouiller</b> : retire la clé de la mémoire. <b>Se déconnecter</b> : met fin à cette session ; la clé reste sur cet appareil.{" "}
          <b>Se déconnecter partout</b> : met fin à vos sessions sur tous les appareils. <b>Oublier cet appareil</b> : supprime votre clé et tout ce que Vrelis
          a stocké dans ce navigateur.
        </li>
        <li>Une connexion dure {l.sessionDays} jours.</li>
      </ul>

      <h2>Ce que Vrelis peut et ne peut pas voir</h2>
      <ul>
        <li>
          <b>Vrelis ne peut pas lire</b> une capsule avant son heure, ni jamais une capsule privée (il n’a aucune clé pour l’ouvrir), ni votre clé, votre PIN
          ou votre phrase.
        </li>
        <li>
          <b>Vrelis voit</b> : votre nom et votre adresse e-mail (le propriétaire et les administrateurs voient l’adresse e-mail), qui envoie une capsule et à
          qui, quand elle a été envoyée, quand une capsule publique s’ouvre, les tailles, et le fait qu’une capsule contient des fichiers. Après son heure, une
          capsule publique peut être lue par tous les membres, donc aussi par Vrelis.
        </li>
        <li>
          Le trousseau et les copies de votre clé ne sont stockés que chiffrés ; Vrelis ne peut pas les ouvrir.
        </li>
        <li>
          Une signature prouve quelle clé a écrit une capsule. Une capsule marquée « clé révoquée » a été signée par une clé que son propriétaire a ensuite
          déclarée volée. Le fait que la clé appartienne à une personne repose sur la liste des membres et sur le nom que l’expéditeur a mis dans la
          capsule. Vérifiez les empreintes avec les personnes à qui vous écrivez en privé.
        </li>
      </ul>

      <h2>Offres et timbres</h2>
      <ul>
        <li>
          Un <b>timbre</b> paie une livraison à un destinataire. Votre propre copie est gratuite. L’offre Gratuite donne {l.freeStamps} timbres par mois,
          {" "}{l.freeStorage} de stockage et {l.fileBytes} de fichiers par message ; l’Ancienne offre
          ({l.legacyStamps} timbres par mois, {l.planStorage}, {l.planFileBytes}) a été accordée aux premiers membres ; Start donnerait {l.planStamps} timbres
          par mois. Les timbres d’un mois ne sont pas reportés, et ils reviennent le
          premier jour du mois suivant (UTC). <b>Facturation</b> montre ce que vous avez, ce qui reste et ce qui est prévu.
        </li>
        <li>
          Pour l’instant, rien n’est facturé et rien ne peut être acheté : les offres sont un aperçu, et un administrateur active une demande manuellement. Ce
          que vous avez déjà envoyé reste lisible par vous et par les personnes à qui vous l’avez envoyé, quoi qu’il
          arrive à votre offre ou à vos timbres.
        </li>
        <li>
          La copie pour vous-même est facultative et activée par défaut. Si vous la désactivez, seul le destinataire pourra lire ce message : vous ne pourrez pas
          le lire vous-même, même après son ouverture.
        </li>
      </ul>

      <h2>Limites du pilote</h2>
      <ul>
        <li>
          Au plus {l.daily} livraisons par jour (votre propre copie est gratuite ; une capsule à un groupe compte une livraison par membre). Envois de fichiers :
          {" "}{l.uploadDaily} et {l.unusedFiles} fichiers inutilisés par 24 heures ;
          les fichiers non utilisés dans une capsule sous 24 heures sont supprimés.
        </li>
        <li>Connexion avec Google, un mot de passe ou une clé d’accès. Pas de paiements, pas de réseau public : les capsules restent à l’intérieur du pilote.</li>
        <li>
          Inviter une adresse qui a déjà un compte envoie à la place à ce membre une demande de mise en relation ; s’il accepte, vous vous suivez mutuellement.
          Votre écran est le même dans les deux cas, donc personne ne peut utiliser les invitations pour savoir qui est membre.
        </li>
      </ul>

      <h2>Pour les développeurs</h2>
      <p>
        Des services peuvent lire et envoyer des capsules via l’<a href="/developers">API de Vrelis</a>. Un service obtient son propre compte grâce à une
        invitation de service d’un administrateur ; pour tous les autres, c’est un membre ordinaire. Il crée sa propre clé et signe lui-même chaque capsule ;
        ses jetons API peuvent lire et envoyer, mais ne peuvent gérer ni les clés ni les modes de connexion. Vous pouvez bloquer ou masquer un service comme
        n’importe qui.
      </p>

      <h2>Ce que nous demandons aux participants au pilote</h2>
      <ol>
        <li>Envoyez une capsule publique qui s’ouvre dans une heure, et une capsule privée à un autre membre qui s’ouvre dans un jour.</li>
        <li>Lisez les capsules publiques après leur ouverture, et essayez d’en lire une avant : vous ne devriez pas pouvoir.</li>
        <li>L’un d’entre vous : configurez votre clé sur un deuxième appareil et lisez-y votre Boîte de réception.</li>
        <li>L’un d’entre vous : masquez un autre membre et vérifiez que ses capsules disparaissent uniquement de votre Fil public.</li>
      </ol>

      <h2>Problèmes</h2>
      <p>
        Utilisez <b>Signaler un problème</b> en bas de chaque page, ou écrivez à {link}. Dites ce que vous avez fait, ce que vous attendiez et ce qui s’est
        passé ; une capture d’écran aide.
        Le service, ses limites et les responsabilités sont décrits dans les <a href="/terms">Conditions d’utilisation</a>.
      </p>
    </>
  );
}
